import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save, Loader2, Video, ExternalLink } from 'lucide-react';
import { Section, Field, Modal, ImageUpload } from './_shared';

const empty = { youtube_url: '', title: '', description: '', thumbnail_url: '', thumbnail_base64: '', order: 0 };

export default function MediaPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = async () => { setLoading(true); try { const r = await cx.get('/media'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const autofill = async () => {
    if (!editing?.youtube_url) { toast.error('Paste a YouTube URL first'); return; }
    setPreviewBusy(true);
    try {
      const { data } = await cx.post('/media/preview', { youtube_url: editing.youtube_url });
      setEditing({
        ...editing,
        title: editing.title || data.title || '',
        description: editing.description || data.description || '',
        thumbnail_url: editing.thumbnail_url || data.thumbnail_url || '',
      });
      toast.success('Autofilled from YouTube');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not fetch YouTube data');
    } finally { setPreviewBusy(false); }
  };

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at; delete payload.youtube_id;
      if (editing.id) await cx.put(`/media/${editing.id}`, payload);
      else await cx.post('/media', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try { await cx.delete(`/media/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Section title="Media (YouTube)" action={<button data-testid="admin-media-add" onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus size={14} /> Add video</button>}>
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No videos yet.</p> : (
        <div className="space-y-3">
          {items.map((v) => (
            <div key={v.id} data-testid={`admin-media-row-${v.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="w-28 aspect-video rounded-md overflow-hidden surface-2 shrink-0">
                {v.thumbnail_url ? <img src={v.thumbnail_url} className="photo-fit" alt="" /> : <div className="w-full h-full flex items-center justify-center"><Video size={20} className="text-muted" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-prim">{v.title || v.youtube_url}</p>
                {v.description && <p className="text-sec text-sm mt-1 line-clamp-2">{v.description}</p>}
                <a href={`https://youtu.be/${v.youtube_id}`} target="_blank" rel="noreferrer" className="text-xs text-brand-teal-light hover-line inline-flex items-center gap-1 mt-1"><ExternalLink size={11} /> {v.youtube_id}</a>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(v)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(v.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit video' : 'Add YouTube video'}>
          <div className="space-y-4">
            <Field label="YouTube URL">
              <div className="flex gap-2">
                <input className="lux-input flex-1" placeholder="https://www.youtube.com/watch?v=…" value={editing.youtube_url} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} />
                <button onClick={autofill} disabled={previewBusy} type="button" className="btn-secondary text-sm whitespace-nowrap">
                  {previewBusy ? <Loader2 size={14} className="animate-spin" /> : 'Auto-fill'}
                </button>
              </div>
              <p className="text-xs text-muted mt-2">Paste URL then click Auto-fill to pull title + thumbnail + a draft description.</p>
            </Field>
            {editing.thumbnail_url && (
              <div className="w-full aspect-video rounded-md overflow-hidden border border-subtle">
                <img src={editing.thumbnail_url} alt="" className="photo-fit" />
              </div>
            )}
            <Field label="Title"><input className="lux-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Short description (editable)"><textarea rows={3} className="lux-input resize-none" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <Field label="Custom thumbnail URL (optional)"><input className="lux-input" value={editing.thumbnail_url} onChange={(e) => setEditing({ ...editing, thumbnail_url: e.target.value })} /></Field>
            <ImageUpload value={editing.thumbnail_base64} onChange={(v) => setEditing({ ...editing, thumbnail_base64: v })} label="Or upload custom thumbnail" />
            <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-media-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
